<template>
  <slc-app-bar
    v-show="!(query.src === 'app')"
    :app-bar-height="appBarHeight"
    :avatar-size="avatarSize"
    :background-color="getColor"
    :close-mobile-search-button="closeMobileSearchButton"
    color="black"
    :dpa-id-profile-url="`${dpaIdURL}/settings?service=dpa-Select`"
    :dpa-select-token="token() || ''"
    :extended="!isSettings && !hideExtension"
    :is-auth-loading="authIsLoading"
    :is-authenticated="isAuthenticated"
    :left-width="leftWidth"
    :left="left"
    :mobile-search-active="mobileSearch"
    :profile="profile"
    :right-width="rightWidth"
    :right="right"
    :resize-header="resizeHeader"
    :show-back-arrow="showBackArrow"
    :show-menu="showMenu"
    @app-bar:back="handleBackButtonClick"
    @closeMobileSearch="toggleMobileSearch"
    @toggleLeftNav="setLeft(!left)"
    ref="slcAppBar"
    v-resize="onResize"
    v-bind="appBar"
  >
    <cdk-search-provider
      v-if="!hideSearch && $refs.slcAppBar"
      slot="search"
      class="SlcAppBar-search"
      :mobile-search="mobileSearch"
      :container-width="appBarWidth"
      :container-height="$refs.slcAppBar.$el.offsetHeight"
      :width="searchMaxWidth"
      :app-bar-color="getColor"
    />
    <template slot="search" v-if="!(!hideSearch && $refs.slcAppBar)">
      <v-spacer />
      <v-icon :color="getTextColor()" @click.native="toggleMobileSearch">
        search
      </v-icon>
    </template>

    <!-- To close FRC in mobile/tablet view -->
    <div slot="closeMobileSearchButton" ref="mobileSearchClose">
      <span
        v-show="mobileSearch"
        :class="
          `${getTextColor({
            dark: 'grey',
            light: 'white'
          })}--text text--darken-3 ml-3 font-weight-medium`
        "
        @click="toggleMobileSearch"
      >
        {{ $t("cancel") }}
      </span>
    </div>

    <v-layout slot="extension">
      <div
        class="SlcAppBar-submit"
        v-if="left && !hideSearch && !mobileSearch"
      />
      <v-layout
        :style="
          !$vuetify.breakpoint.xsOnly ? 'padding-left: 0 !important;' : ' '
        "
        :class="$vuetify.breakpoint.xsOnly ? '' : 'container'"
        align-center
      >
        <slc-date-range-picker />
        <slc-pagination-provider />
        <v-spacer />
        <component :is="viewOptionSwitcher" />
      </v-layout>
    </v-layout>
  </slc-app-bar>
</template>
<script>
/* eslint-disable camelcase */
import { defineComponent, provide, computed, inject } from "vue";
import { mapState, mapGetters, mapMutations } from "vuex";
import { format, subDays } from "date-fns";
import resize from "vuetify/es5/directives/resize";
import { DATE_FORMAT_ISO } from "@/constants/theme";
import useAuthentication from "@/composition/useAuthentication";
import useFilterUtils from "@/composition/useFilterUtils";
import SlcAppBar from "@/components/SlcAppBar/SlcAppBar.vue";
import CdkSearchProvider from "@/components/CdkSearch/CdkSearchProvider.vue";
import SlcDateRangePicker from "@/components/SlcDateRangePicker/SlcDateRangePicker.vue";
import SlcPaginationProvider from "@/components/SlcPagination/SlcPaginationProvider.vue";
import { contrastingTextToBackgroundColor as getTextColor } from "@/utils/utils-color";
import {
  backToHomeRoutes,
  backButtonNavigationRoutes as routesWithBack
} from "@/router";
import store from "@/store/";
import { isMobile, isTablet } from "@/utils/responsive";
import SlcViewOptionTabs from "@/components/SlcViewOptions/SlcViewOptionTabs.vue";
import SlcViewOptionMenu from "@/components/SlcViewOptions/SlcViewOptionMenu.vue";

export default defineComponent({
  setup() {
    const eventBus = inject("eventBus");

    const {
      dpaIdURL,
      validateAndRetrieveToken,
      authIsLoading,
      isAuthenticated,
      logout,
      status
    } = useAuthentication();

    const { saveFilterIsActivated } = useFilterUtils();

    const right = computed(() => store.state.ui.right);
    const left = computed(() => store.state.ui.left);
    const rightWidth = computed(() => store.state.ui.rightWidth);
    const leftWidth = computed(() => store.state.ui.leftWidth);

    provide("right", right);
    provide("left", left);
    provide("rightWidth", rightWidth);
    provide("leftWidth", leftWidth);

    return {
      dpaIdURL,
      token: validateAndRetrieveToken,
      authIsLoading,
      isAuthenticated,
      logout,
      saveFilterIsActivated,
      status,
      timeout: 72000000,
      right,
      rightWidth,
      left,
      leftWidth,
      eventBus
    };
  },
  computed: {
    ...mapState("route", ["path", "params", "query"]),
    ...mapState("route", {
      filterId: ({ query }) => query.filterId,
      filterCombination: ({ query }) => !!query.filterCombination,
      appBar: ({ meta }) => meta.appBar
    }),
    ...mapGetters("filters", ["getColorById", "getNameById", "getFilterById"]),
    ...mapState("user", ["profile"]),
    path() {
      return this.$route.path;
    },
    params() {
      return this.$route.params;
    },
    query() {
      return this.$route.query;
    },
    composerQuery() {
      return this.query.composer;
    },
    filterId() {
      return this.query.filterId;
    },
    filterCombination() {
      return !!this.query.filterCombination;
    },
    showBackArrow() {
      if (this.filterId) {
        return true;
      }
      if (this.path.match(routesWithBack)) {
        return true;
      }
      if (this.filterCombination) {
        return true;
      }
      return false;
    },
    showMenu() {
      return !this.showBackArrow;
    },
    isSettings() {
      return !!this.path.match(/\/settings/);
    },
    hideSearch() {
      return (
        this.isSettings ||
        (isMobile(this) && !this.mobileSearch) ||
        this.path.match(backToHomeRoutes)
      );
    },
    hideExtension() {
      return !!this.path.match(backToHomeRoutes);
    },
    // Gets filter color if set, else defaults to passed prop.
    getColor() {
      if (this.isSettings) {
        return "#fff";
      }

      if (this.filterId) {
        return this.getColorById(this.filterId);
      }
      return "#fff";
    },
    // Adds 'modified' string to title if filter has changed.
    getTitle() {
      if (this.isSettings) {
        return this.$t("Preferences");
      }

      const { filterCombination } = this.$route.query;
      const { category } = this.$route.query;

      if (this.filterId) {
        const filterName = this.getNameById(this.filterId);
        return `${filterName}${
          this.saveFilterIsActivated ? " (geändert)" : ""
        }`;
      }
      if (filterCombination !== undefined) {
        switch (filterCombination) {
          case "public":
            if (category === "external") {
              return "Weitere Filter";
            }
            return "Filter der dpa";
          case "user":
            return "Gespeicherte Suchen";
          default:
            return "";
        }
      }
      return "dpa select";
    },
    backFromFilter() {
      const { path, params, query } = this.$route;
      const newQuery = { ...query };
      delete newQuery.filterId;

      return {
        path,
        params,
        query: newQuery
      };
    },
    avatarSize() {
      if (isMobile(this)) {
        return 36;
      }
      return 48;
    },
    appBarHeight() {
      if (isTablet(this)) {
        return 56;
      }
      return 64;
    },

    // EC-603 width and max-width of search component should be restricted
    // to the width of the remaining space in the parent container
    searchMaxWidth() {
      const appBarPadding = 32;
      const desktopSearchOffset = 412;

      return (
        this.appBarWidth -
        (this.mobileSearch
          ? this.mobileSearchCloseWidth + appBarPadding
          : desktopSearchOffset)
      );
    },

    viewOptionSwitcher() {
      if (isMobile(this)) {
        return SlcViewOptionMenu;
      }

      return SlcViewOptionTabs;
    }
  },
  components: {
    CdkSearchProvider,
    SlcAppBar,
    SlcPaginationProvider,
    SlcViewOptionTabs,
    SlcDateRangePicker
  },
  props: {
    resizeHeader: {
      type: Boolean,
      default: false
    }
  },
  directives: { resize },
  methods: {
    ...mapMutations("ui", ["setLeft"]),
    handleBackButtonClick() {
      if (this.filterId && !this.isSettings) {
        this.$router.push(this.backFromFilter);
        this.eventBus.$emit("refresh-feed");
      } else if (this.path.match(backToHomeRoutes)) {
        this.$router.push({
          path: "/newsitems"
        });
      } else if (this.isSettings) {
        this.$router.push(this.backFromSettings);
      } else {
        this.$router.back();
      }
    },
    toggleMobileSearch() {
      this.eventBus.$emit("frc-close");

      const wasOpen = Boolean(this.mobileSearch);
      if (wasOpen) {
        this.eventBus.$emit("activateFrcGradient", {
          isTop: false,
          isBottom: false
        });
      }

      setTimeout(
        () => {
          this.mobileSearch = !this.mobileSearch;
        },
        wasOpen ? 300 : 0
      );
    },
    async onResize() {
      await this.$nextTick();
      this.reflowSearch();
      this.appBarWidth = this.$refs.slcAppBar.$el.offsetWidth;
    },
    reflowSearch() {
      // EC-603 check whether the FRC was previously open before switching breakpoints
      const wasMobileFrcOpen = Boolean(this.mobileSearch);
      const wasDesktopFrcOpen =
        Boolean(!this.closeMobileSearchButton) && !isMobile(this);
      setTimeout(() => {
        if (!isMobile(this)) {
          if (wasMobileFrcOpen) {
            this.eventBus.$emit("frc-close");
            this.eventBus.$emit("activateFrcGradient", {
              isTop: false,
              isBottom: false
            });
          }

          this.mobileSearch = false;
        } else if (wasDesktopFrcOpen) {
          this.mobileSearch = true;
        }
      }, 200);
    },
    getTextColor() {
      return getTextColor({ hex: this.getColor });
    },
    // Mirrors the changes to the FRC open/closed state in the URL.
    // NB: The functionality for the mobile FRC in this component and
    // for the desktop FRC in CdkSearch is duplicated. The functionality for both
    // should probably be moved into this component, as it's always present.
    updateComposerInUrl() {
      const isQueryPresent = this.composerQuery === "open";
      // If the FRC is open and the 'composer: open' query is missing...
      if (this.mobileSearch && !isQueryPresent) {
        // adds 'composer: open' to the current query without mutating
        this.$router.replace({
          query: { ...this.$route.query, composer: "open" }
        });
        // If the FRC is closed and the 'component: open' query is present...
      } else if (!this.mobileSearch && isQueryPresent && isMobile(this)) {
        const query = { ...this.$route.query };
        delete query.composer;
        // replaces the query object by a version without 'composer: open'
        this.$router.replace({ query });
      }
    }
  },
  data() {
    return {
      backFromSettings: {
        path: "/newsitems",
        params: {},
        query: {}
      },
      mobileSearch: false,
      closeMobileSearchButton: true,
      mobileSearchCloseWidth: 0,
      appBarWidth: 0
    };
  },
  watch: {
    $route: {
      handler(currentRoute, oldRoute) {
        const previousRoute = oldRoute || this.backFromSettings;
        const currentPath = currentRoute.path;
        const oldPath = previousRoute?.path;
        const shouldAddDateParamsToRoute =
          this.$route.params.viewMode === "list" &&
          !this.$route.query.startDate &&
          !this.$route.query.endDate;

        if (currentPath.match(/\/settings/) && oldPath.match(/\/newsitems/)) {
          this.backFromSettings = {
            ...previousRoute
          };
        }

        if (shouldAddDateParamsToRoute) {
          this.$router.replace({
            query: {
              ...this.$route.query,
              startDate: format(subDays(new Date(), 40), DATE_FORMAT_ISO),
              endDate: format(new Date(), DATE_FORMAT_ISO)
            }
          });
        }
      },
      immediate: true
    },
    async mobileSearch() {
      if (this.mobileSearch) {
        await this.$nextTick();
        this.mobileSearchCloseWidth = this.$refs.mobileSearchClose.getBoundingClientRect().width;
      }
      this.updateComposerInUrl();
    },
    composerQuery: {
      immediate: true,
      handler() {
        // If the 'composer: open' query was added but the FRC isn't open...
        if (
          this.composerQuery === "open" &&
          !this.mobileSearch &&
          isMobile(this)
        ) {
          // ...open it
          this.toggleMobileSearch();
        }
      }
    }
  },
  mounted() {
    this.eventBus.$on("frc-toggled", frcOpen => {
      this.closeMobileSearchButton = !frcOpen;
    });

    this.eventBus.$on("close-mobile-frc", () => {
      this.mobileSearch = false;
    });

    this.eventBus.$on("contextual-navigation", () => {
      if (this.hideSearch) {
        this.mobileSearch = true;
      }
    });

    this.appBarWidth = this.$refs.slcAppBar.$el.offsetWidth;
  }
});
</script>
<style lang="postcss" scoped></style>
